import React from 'react';
import {
    getElementId,
    getImageProp,
    getMediaHeight,
    getMediaWidth,
    getWidth,
} from '../../../common/elements/utils/elementPropertyUtils';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { getData } from '../attachments/attachmentsSelector';
import { getImageAspectRatio, getImageDefaultMaxWidth } from './imageElementUtils';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ResizeChanges, SetElementSizeAndPosition } from '../resizing/resizeTypes';
import { analyticsEvent } from '../../analytics';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { DispatchUpdateElement } from '../actions/elementActionTypes';

type RequiredImageProps = {
    element: ImMNElement;
    dispatchUpdateElement: DispatchUpdateElement;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    attachment: any;
    gridSize: number;
};

const imageDoubleClickResizeDecorator = <T extends RequiredImageProps>(
    DecoratedComponent: React.ComponentType<T>,
): React.FC<T> => {
    return (props) => {
        const setElementSizeAndPosition = (changes: ResizeChanges, position?: Point) => {
            analyticsEvent('resized-image');
            props.setElementSizeAndPosition(changes, position);
        };

        const handleDoubleClick = (event: MouseEvent) => {
            const { element, dispatchUpdateElement, attachment, gridSize } = props;

            event.preventDefault();
            event.stopPropagation();

            const imageData = getImageProp(element);
            const attachmentData = getData(attachment);
            const hasImage = imageData || attachmentData;

            // Ensure we have dimensions to match before changing size
            if (!hasImage) return;

            const hasMediaDimensions = getMediaWidth(element) && getMediaHeight(element);

            if (hasMediaDimensions) {
                // Remove the forced aspect ratio
                return dispatchUpdateElement({
                    id: getElementId(element),
                    changes: {
                        media: null,
                    },
                });
            }

            const savedWidth = getWidth(element) || ELEMENT_DEFAULT_WIDTH;

            let width = ELEMENT_DEFAULT_WIDTH;

            const defaultMaxWidth = getImageDefaultMaxWidth({ element, attachmentData });
            const aspectRatio = getImageAspectRatio({ element, attachmentData });

            const height = width / aspectRatio;

            // Only toggle to the default width of the media if we're currently at the default element width
            if (savedWidth === ELEMENT_DEFAULT_WIDTH) {
                width = defaultMaxWidth / gridSize;
            }

            setElementSizeAndPosition({ width, height });
        };

        return (
            <DecoratedComponent
                {...props}
                setElementSizeAndPosition={setElementSizeAndPosition}
                handleDoubleClick={handleDoubleClick}
            />
        );
    };
};

export default imageDoubleClickResizeDecorator;
